import React from 'react'
import type { ProjectDetails } from '../../../types'
import type { HubProject } from '../../../api/data360'

export function MetaChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 text-[10px] font-medium text-blueprint-blue">
      {children}
    </span>
  )
}

export function ProjectHubListColgroup() {
  return (
    <colgroup>
      <col className="w-[32%]" />
      <col className="w-[16%]" />
      <col className="w-[28%]" />
      <col className="w-[12%]" />
      <col className="w-[12%]" />
    </colgroup>
  )
}

export const PROJECT_HUB_LIST_TH_CLASS =
  'px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-heritage-grey'

export const PROJECT_HUB_NAME_CLASS =
  'truncate font-heading text-[14px] font-semibold text-blueprint-blue'

export const PROJECT_HUB_META_CLASS = 'truncate text-[11px] text-heritage-grey'

export const PROJECT_HUB_LIST_TD_CLASS = 'px-4 py-2.5 align-middle'

export const PROJECT_HUB_ROW_HOVER =
  'transition-all duration-200 hover:bg-white hover:shadow-[0_8px_24px_rgba(0,39,63,0.08)]'

/** Hub count must match project interior — use local `pipeSets` only. */
export function pipeSetLabel(project: ProjectDetails): string {
  const local = project.pipeSets?.length ?? 0
  return `${local} pipe set${local === 1 ? '' : 's'}`
}

export interface ProjectDisplayFields {
  openPath: string
  client: string
  application: string
  location: string
  pipeSets: string
  status: string
  metaLine: string
}

export function getProjectDisplayFields(project: ProjectDetails): ProjectDisplayFields {
  const openPath = `/projects/${project.id}/next`
  const client = project.endClients?.[0] ?? project.client ?? '—'
  const application =
    project.applications?.filter(Boolean).join(', ') || project.application || '—'
  const location = project.location?.trim() || '—'
  const pipeSets = pipeSetLabel(project)
  const status = project.status || 'Active'

  return {
    openPath,
    client,
    application,
    location,
    pipeSets,
    status,
    metaLine: [application, location, pipeSets].join(' · ')
  }
}

export function getHubProjectDisplayFields(project: HubProject): Omit<ProjectDisplayFields, 'openPath'> {
  const base = getProjectDisplayFields({
    ...project,
    id: project.wpsShadowId || project.id,
    pipeSets: project.pipeSets ?? []
  })
  return {
    client: base.client,
    application: base.application,
    location: base.location,
    pipeSets: base.pipeSets,
    status: base.status,
    metaLine: base.metaLine
  }
}

export function handleRowKeyDown(
  e: React.KeyboardEvent<HTMLElement>,
  onActivate: () => void
): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    onActivate()
  }
}
